/**
 * useArrayWithStableKeys
 * ----------------------
 * Utilities + hook to permanently eliminate the "array index as React key" bug
 * class in dynamic list forms (line items, payments, family members, etc.).
 *
 * Every item is tagged with a stable, opaque `_uid`. Render with
 * `key={item._uid}` and mutate by `_uid` (never by array index) so that
 * adding / removing / reordering rows never reattaches input state, focus or
 * animations to the wrong row.
 *
 * Two ways to use it:
 *   1. Standalone array state -> `useArrayWithStableKeys(initial)`.
 *   2. Arrays nested inside a larger formData object -> use the helpers
 *      `withStableUid`, `attachStableUids` and `stripStableKeys` directly.
 */
import { useState, useCallback } from 'react';

/** Generate a collision-resistant, stable unique id for a list row. */
export const genStableUid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `uid_${Date.now()}_${Math.random().toString(36).slice(2)}`;

/** Ensure a single item carries a `_uid` (keeps an existing one). */
export const withStableUid = (item = {}) =>
  item && item._uid ? item : { ...item, _uid: genStableUid() };

/** Map an array, guaranteeing every item has a stable `_uid`. */
export const attachStableUids = (items = []) => (items || []).map(withStableUid);

/**
 * Remove internal `_uid` (and any extra transient display fields) before
 * sending the data to an API.
 */
export const stripStableKeys = (items = [], extraKeys = []) =>
  (items || []).map((item) => {
    const clone = { ...item };
    delete clone._uid;
    extraKeys.forEach((k) => delete clone[k]);
    return clone;
  });

export function useArrayWithStableKeys(initial = []) {
  const [items, setItemsRaw] = useState(() => attachStableUids(initial));

  const setItems = useCallback((next) => {
    setItemsRaw((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      return attachStableUids(resolved);
    });
  }, []);

  const add = useCallback((item = {}) => {
    const next = withStableUid(item);
    setItemsRaw((prev) => [...prev, next]);
    return next._uid;
  }, []);

  const removeByUid = useCallback((uid) => {
    setItemsRaw((prev) => prev.filter((it) => it._uid !== uid));
  }, []);

  const updateByUid = useCallback((uid, patch) => {
    setItemsRaw((prev) =>
      prev.map((it) =>
        it._uid === uid
          ? { ...it, ...(typeof patch === 'function' ? patch(it) : patch) }
          : it,
      ),
    );
  }, []);

  const replaceAll = useCallback((nextItems = []) => {
    setItemsRaw(attachStableUids(nextItems));
  }, []);

  const reset = useCallback(() => setItemsRaw([]), []);

  /** Items without the internal `_uid`, ready for submission. */
  const toPayload = useCallback((extraKeys = []) => stripStableKeys(items, extraKeys), [items]);

  return { items, setItems, add, removeByUid, updateByUid, replaceAll, reset, toPayload };
}

export default useArrayWithStableKeys;
